import { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect, forwardRef, useImperativeHandle, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import { type MentionCandidate, buildCandidates } from './mentionUtils'
import { createDebug, createNadDebug } from '../../utils/debugLog'
import { useAuthOptional } from '../../contexts/authContextRaw'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'
import {
  createMentionChipElement,
  serializeNodeText,
  getPlainTextFromEditor,
  getReferencedNodeIdsFromEditor,
  selectionInsideEditor,
  placeCaretAtEnd,
  hydrateContent,
  MentionDropdownPortal,
} from './contentEditableUtils'
import { UploadFlyout } from '../shared/UploadFlyout'
import type { ClassifiedMediaFile } from '../../utils/uploadFileValidation'
import {
  composePromptWithDocuments as composeWithDocs,
  type DocumentAttachment,
} from '../shared/documentPrompt'
import { useTranslation } from 'react-i18next'
import { AtSign, FileText, X } from 'lucide-react'
import { SkillPicker } from './SkillPicker'
import { trackChatUploadMenuViewed, trackSkillChipCleared, trackSlashSkillSelected } from '../../analytics/chatHeaderAnalytics'
import { trackInteraction } from '../../analytics/interactionAnalytics'
import { noteChatReference } from '../../analytics/chatReferenceOrigin'
import { countBucket } from '../../analytics/buckets'
import {
  directorSkillPublicSlug,
  directorSkillSerializedMarker,
  directorSkillSerializedMarkers,
  listAvailableDirectorSkills,
  type DirectorSkillSummary,
} from '../../api/directorSkills'
import { useBooleanFlagValue } from '../../hooks/useBooleanFlagValue'
import { useNuxTooltipEvents } from '../../onboarding/nuxEvents'

const mentionLog = createDebug('dw:mention-trigger:chat-input')
const composerHoverLog = createNadDebug('dw:director-composer-icon-hover:hover')
const composerLog = createNadDebug('dw:chat:composer')
const landingReferenceLog = createNadDebug('dw:landing-upload:references', { priority: true })
const directorSkillLog = createNadDebug('dw:director-skills:composer')
const COMPOSER_TRIGGER_CLASS = 'p-2 rounded-md text-text-muted hover:text-text-secondary hover:bg-chrome/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome transition-colors disabled:opacity-50'

function logComposerTriggerHover(trigger: 'upload' | 'mention' | 'skill') {
  composerHoverLog({
    event: 'hover',
    trace_id: 'director-composer-icon-hover:controls',
    trigger,
    tint: 'bg-chrome/20',
  })
}

function createDirectorSkillChipElement(
  skill: DirectorSkillSummary,
): HTMLSpanElement {
  const chip = document.createElement('span')
  chip.contentEditable = 'false'
  chip.dataset.skillChip = 'true'
  chip.dataset.skillRef = skill.ref
  chip.dataset.skillVersionId = skill.versionId
  chip.dataset.skillName = directorSkillPublicSlug(skill)
  chip.dataset.skillDisplayName = skill.displayName
  chip.className =
    'group mx-1 inline-flex max-w-full items-center gap-1.5 rounded-md border border-input-border bg-surface-1 px-2 py-0.5 text-sm text-foreground align-baseline'
  chip.setAttribute('data-testid', 'director-skill-selected-chip')

  const icon = document.createElement('span')
  icon.className = 'inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground'
  icon.setAttribute('aria-hidden', 'true')
  // Keep the token icon identical to SkillPicker's current WandSparkles icon.
  icon.innerHTML = '<svg class="lucide lucide-wand-sparkles h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m21.64 3.64-1.28-1.28a1.25 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"/><path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/></svg>'

  const name = document.createElement('span')
  name.className = 'truncate'
  name.textContent = skill.displayName
  chip.setAttribute('aria-label', skill.displayName)

  const remove = document.createElement('button')
  remove.type = 'button'
  remove.contentEditable = 'false'
  remove.dataset.skillChipRemove = 'true'
  remove.className =
    'rounded text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/50'
  remove.setAttribute('aria-label', `Remove ${skill.displayName}`)
  remove.textContent = '\u00d7'

  chip.append(icon, name, remove)
  return chip
}

function removeDirectorSkillChips(editor: HTMLElement) {
  editor.querySelectorAll('[data-skill-chip="true"]').forEach((chip) => {
    const spacer = chip.nextSibling
    chip.remove()
    if (spacer instanceof Text && /^[ \u00a0]/.test(spacer.data)) {
      spacer.deleteData(0, 1)
      if (spacer.data.length === 0) spacer.remove()
    }
  })
}

function replaceDirectorSkillChip(
  editor: HTMLElement,
  skill: DirectorSkillSummary | null,
) {
  const existing = editor.querySelector<HTMLElement>('[data-skill-chip="true"]')
  if (!skill) {
    removeDirectorSkillChips(editor)
    return
  }

  const chip = createDirectorSkillChipElement(skill)
  if (existing) {
    existing.replaceWith(chip)
    const selection = window.getSelection()
    if (selection) {
      const range = document.createRange()
      range.setStartAfter(chip)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }
    return
  }

  const selection = window.getSelection()
  if (!selection || !selectionInsideEditor(editor, selection) || selection.rangeCount === 0) {
    placeCaretAtEnd(editor)
  }
  const activeSelection = window.getSelection()
  if (!activeSelection || activeSelection.rangeCount === 0) {
    editor.append(chip)
    return
  }
  const range = activeSelection.getRangeAt(0)
  range.deleteContents()
  const spacer = document.createTextNode(' ')
  range.insertNode(spacer)
  range.insertNode(chip)
  range.setStartAfter(spacer)
  range.collapse(true)
  activeSelection.removeAllRanges()
  activeSelection.addRange(range)
}

function getDirectorSkillPosition(
  editor: HTMLElement,
  chip: HTMLElement,
  serializedEditor = serializeNodeText(editor, editor).replace(/\u00A0/g, ' '),
): number {
  const range = document.createRange()
  range.setStart(editor, 0)
  range.setEndBefore(chip)
  const container = document.createElement('div')
  container.appendChild(range.cloneContents())
  const serializedPrefix = serializeNodeText(container, container).replace(/\u00A0/g, ' ')
  const leadingTrimLength = serializedEditor.length - serializedEditor.trimStart().length
  return Math.min(
    Math.max(serializedPrefix.length - leadingTrimLength, 0),
    serializedEditor.trim().length,
  )
}

function hydrateDirectorSkillDraft(
  editor: HTMLElement,
  content: string,
  candidates: MentionCandidate[],
  skill: DirectorSkillSummary,
  projectId?: string,
  position?: number,
) {
  const markers = directorSkillSerializedMarkers(skill)
  const preferredPosition = position === undefined
    ? -1
    : Math.min(Math.max(position, 0), content.length)
  const preferredMarker = markers.find(
    (marker) => preferredPosition >= 0
      && content.slice(preferredPosition, preferredPosition + marker.length) === marker,
  )
  const indexedMarker = markers
    .map((marker) => ({ marker, index: content.indexOf(marker) }))
    .find((candidate) => candidate.index >= 0)
  const markerPosition = preferredMarker
    ? preferredPosition
    : indexedMarker?.index ?? -1
  const matchedMarker = preferredMarker ?? indexedMarker?.marker
  const insertionPosition = markerPosition >= 0
    ? markerPosition
    : preferredPosition >= 0
      ? preferredPosition
      : content.length
  const suffixPosition = markerPosition >= 0
    ? markerPosition + (matchedMarker?.length ?? 0)
    : insertionPosition

  const prefix = document.createElement('div')
  const suffix = document.createElement('div')
  hydrateContent(prefix, content.slice(0, insertionPosition), candidates, projectId)
  hydrateContent(suffix, content.slice(suffixPosition), candidates, projectId)

  editor.innerHTML = ''
  editor.append(
    ...Array.from(prefix.childNodes),
    createDirectorSkillChipElement(skill),
    ...Array.from(suffix.childNodes),
  )
}

interface SkillTypeaheadPortalProps {
  skills: DirectorSkillSummary[]
  highlightIndex: number
  editorRef: React.RefObject<HTMLElement | null>
  dropdownRef: React.RefObject<HTMLDivElement>
  label: string
  onSelect: (skill: DirectorSkillSummary) => void
  onHighlight: (index: number) => void
}

function SkillTypeaheadPortal({
  skills,
  highlightIndex,
  editorRef,
  dropdownRef,
  label,
  onSelect,
  onHighlight,
}: SkillTypeaheadPortalProps) {
  const [editorRect, setEditorRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    const editor = editorRef.current
    if (!editor || skills.length === 0) {
      setEditorRect(null)
      return
    }
    setEditorRect(editor.getBoundingClientRect())
  }, [editorRef, skills.length])

  if (!editorRect || skills.length === 0) return null

  return createPortal(
    <div
      ref={dropdownRef}
      id="chat-skill-dropdown"
      role="listbox"
      aria-label={label}
      data-testid="skill-typeahead-dropdown"
      data-canvas-interaction-ignore=""
      className="fixed z-[9999] max-h-[240px] overflow-y-auto rounded-lg border border-border bg-background shadow-xl"
      style={{
        top: editorRect.top - 4,
        left: editorRect.left,
        width: editorRect.width,
        transform: 'translateY(-100%)',
      }}
    >
      {skills.map((skill, index) => (
        <button
          key={`${skill.ref}@${skill.versionId}`}
          type="button"
          role="option"
          aria-selected={index === highlightIndex}
          data-skill-typeahead-item="true"
          data-testid={`skill-typeahead-option-${skill.skillId}`}
          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
            index === highlightIndex ? 'bg-chrome/15' : 'hover:bg-surface-2'
          }`}
          onMouseDown={(event) => {
            event.preventDefault()
            onSelect(skill)
          }}
          onMouseEnter={() => onHighlight(index)}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-chrome/30 bg-chrome/10 text-sm font-medium text-chrome">
            /
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">
              {skill.displayName}
            </span>
            <span className="block truncate text-xs font-medium text-muted-foreground">
              /{directorSkillPublicSlug(skill)}
            </span>
          </span>
        </button>
      ))}
    </div>,
    document.body,
  )
}

type ChatInputStatus = 'idle' | 'running' | 'polling' | 'pending_questions'

/** Attachment for chat messages (RFC 0054) */
export interface ChatImageAttachment {
  type: 'image'
  file?: File
  previewUrl: string
  path?: string  // Path to uploaded asset (when selected from gallery)
  name?: string
}

/**
 * Document attachment + prompt composition now live in
 * `shared/documentPrompt.ts` so the landing composer can reuse them without
 * importing this component. Re-exported here because existing call sites and
 * tests import them from ChatInput.
 */
export type { DocumentAttachment as ChatDocumentAttachment } from '../shared/documentPrompt'
export {
  composePromptWithDocuments,
  neutralizeDocumentDelimiters,
} from '../shared/documentPrompt'

export type ChatAttachment = ChatImageAttachment | DocumentAttachment

/* ------------------------------------------------------------------ */
/*  ChatInput                                                          */
/* ------------------------------------------------------------------ */

export interface ChatInputHandle {
  setText: (
    text: string,
    draft?: {
      selectedSkill: DirectorSkillSummary | null
      selectedSkillPosition?: number
    },
  ) => void
  addMention: (nodeId: string) => void
  /** Focus the editor without changing its content (caret moves to end). */
  focus: () => void
}

export type ChatSendDisposition = 'sent' | 'queued'

interface CanvasComposerDraft {
  text: string
  skill: DirectorSkillSummary | null
  skillPosition?: number
  documents: DocumentAttachment[]
  pendingDocumentName: string | null
}
// Kept in memory only during the full-page 3D editing detour. Never stores HTML.
const deskReturnDrafts = new Map<string, CanvasComposerDraft>()
// Media kinds a composer upload can carry, for chat_attachment_added.
const UPLOAD_MEDIA_KINDS: ReadonlyArray<'image' | 'video' | 'audio'> = ['image', 'video', 'audio']

interface ChatInputProps {
  /** Reuse the same composer before a project exists, with landing-owned controls. */
  embedded?: {
    /** Authoritative temporary references before canvas nodes exist. */
    mentionCandidates?: MentionCandidate[]
    editorClassName: string
    /** Classes for the Skills trigger so it matches the host's toolbar chrome. */
    skillTriggerClassName?: string
    /** Classes for the Skills trigger icon (defaults to the picker's own size). */
    skillTriggerIconClassName?: string
    renderToolbar: (skillPicker: ReactNode, submit: () => void) => ReactNode
    onTextChange: (text: string) => void
    allowEmptySend?: boolean
  }

  deskDraftKey?: string
  onSend: (
    message: string,
    attachments?: ChatAttachment[],
    referencedNodeIds?: string[],
    selectedSkill?: DirectorSkillSummary,
    selectedSkillPosition?: number,
  ) => ChatSendDisposition | false
  /** Retrieve the newest queued prompt when ArrowUp is pressed in an empty editor. */
  onRetrieveQueuedPrompt?: () => boolean
  /** RFC 0061: Callback to stop running execution */
  onStop?: () => void | Promise<void>
  disabled?: boolean
  placeholder?: string
  /** Status for the send button indicator (RFC 0036) */
  status?: ChatInputStatus
  /** Project ID for image picker gallery (RFC 0054) */
  projectId?: string
  /** Canvas nodes for @mention autocomplete */
  canvasNodes?: ReactFlowCanvasNode[]
  /** Called when a mention pill is clicked -- used to focus the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Called with a merged "Upload media" selection. The workspace creates one upload node per accepted file. */
  onUploadMedia?: (accepted: ClassifiedMediaFile[], rejected: File[]) => void
}

/** Shared Director editor with serialized reference and skill chips, including pre-project turns. */
export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ embedded, deskDraftKey: deskScopeKey, onSend, onRetrieveQueuedPrompt, onStop, disabled = false, placeholder, status = 'idle', canvasNodes, onMentionClick, onUploadMedia }, ref) {
  const embeddedRef = useRef(embedded)
  embeddedRef.current = embedded
  const isEmbedded = Boolean(embedded)
  const projectId = useOptionalProjectId()
  if (!embedded && !projectId) throw new Error('ChatInput requires a project outside embedded mode')
  const auth = useAuthOptional()
  const deskDraftKey = deskScopeKey ? `${auth?.user?.id ?? "local"}:${deskScopeKey}` : undefined
  const { t } = useTranslation('chat')
  const { emitNuxEvent } = useNuxTooltipEvents()
  const effectivePlaceholder = placeholder ?? t('chatInput.defaultPlaceholder')
  const [isFocused, setIsFocused] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const editorRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const skillDropdownRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
  const skillAnchorRef = useRef<{ node: globalThis.Node; offset: number } | null>(null)
  const composingRef = useRef(false)
  const isExecutionActive = status === 'running' || status === 'polling'
  const skillsV2Enabled = useBooleanFlagValue('director_skills_v2_enabled', false)
  const [selectedSkill, setSelectedSkill] = useState<DirectorSkillSummary | null>(null)
  const [availableSkills, setAvailableSkills] = useState<DirectorSkillSummary[]>([])

  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionFilter, setMentionFilter] = useState('')
  const [mentionHighlight, setMentionHighlight] = useState(0)
  const [showSkillDropdown, setShowSkillDropdown] = useState(false)
  const [skillFilter, setSkillFilter] = useState('')
  const [skillHighlight, setSkillHighlight] = useState(0)

  useEffect(() => {
    directorSkillLog({
      event: 'director-skills:availability-resolved',
      feature: 'director-skills',
      concern: 'access',
      enabled: skillsV2Enabled,
    })
  }, [skillsV2Enabled])

  // Build mention candidates (with thumbnailPath from canvas-v2 mentionUtils)
  const embeddedCandidates = embedded?.mentionCandidates
  const candidates = useMemo(
    () => embeddedCandidates ?? (canvasNodes ? buildCandidates(canvasNodes) : []),
    [embeddedCandidates, canvasNodes],
  )
  const filteredCandidates = mentionFilter
    ? candidates.filter(
        (c) =>
          c.label.toLowerCase().includes(mentionFilter.toLowerCase()) ||
          c.type.toLowerCase().includes(mentionFilter.toLowerCase()),
      )
    : candidates
  const filteredSkills = useMemo(() => {
    const query = skillFilter.toLocaleLowerCase()
    return query
      ? availableSkills.filter((skill) =>
          `${skill.displayName} ${directorSkillPublicSlug(skill)} ${skill.description}`.toLocaleLowerCase().includes(query),
        )
      : availableSkills
  }, [availableSkills, skillFilter])

  // RFC-1218 PR1: documents attached to THIS turn. Text only — the backend
  // keeps no copy, so removing a chip is a complete undo.
  const [documents, setDocuments] = useState<DocumentAttachment[]>([])
  const [documentPending, setDocumentPending] = useState<string | null>(null)
  const [documentError, setDocumentError] = useState<string | null>(null)
  // Extraction is async, so a send can land while one is in flight. Results
  // are stamped with the turn that requested them; the counter bumps on every
  // send, and a result from a previous turn is dropped rather than silently
  // attached to the next message the user types.
  const documentTurnRef = useRef(0)
  const documentLifetime = useRef(0)
  useEffect(() => {
    const generation = ++documentLifetime.current
    return () => { documentLifetime.current = generation + 1 }
  }, [deskDraftKey])

  const handleDocumentSelected = useCallback(
    async (file: File) => {
      const turn = documentTurnRef.current
      const lifetime = documentLifetime.current
      const isCurrent = () => documentTurnRef.current === turn && documentLifetime.current === lifetime
      setDocumentError(null)
      setDocumentPending(file.name)
      try {
        // Imported lazily, not at module scope. The `components` Vitest project
        // runs with `isolate: false` (shared per-worker module registry), so a
        // new top-level edge from ChatInput into api/client changes module
        // resolution for unrelated files that share the worker — it made
        // canvasLevelImageHistoryPopover issue real fetches in CI. Deferring
        // the import keeps this file's import-time graph exactly as it was.
        const { api } = await import('../../api/client')
        if (!projectId) throw new Error('Chat document extraction requires a project')
        const extracted = await api.extractDocumentText(projectId, file, 'prompt_attachment')
        if (!isCurrent()) return
        if (deskDraftKey) deskReturnDrafts.delete(deskDraftKey)
        setDocuments((previous) => [
          ...previous,
          {
            type: 'document',
            filename: extracted.filename,
            kind: extracted.kind,
            text: extracted.text,
            charCount: extracted.char_count,
            segmentCount: extracted.segment_count,
            clearedImageCount: extracted.cleared_image_count,
          },
        ])
      } catch (error) {
        if (!isCurrent()) return
        // Surface the backend's specific refusal (wrong type / too big / no
        // text layer) verbatim — a generic "upload failed" would hide the one
        // piece of information that tells the user what to do next.
        setDocumentError(error instanceof Error ? error.message : String(error))
      } finally {
        if (isCurrent()) setDocumentPending(null)
      }
    },
    [projectId, deskDraftKey],
  )

  // Track whether editor has content for send button state
  const [hasContent, setHasContent] = useState(false)

  const closeDropdown = useCallback(() => {
    anchorRef.current = null
    setShowMentionDropdown(false)
    setMentionFilter('')
    setMentionHighlight(0)
  }, [])

  const closeSkillDropdown = useCallback(() => {
    skillAnchorRef.current = null
    setShowSkillDropdown(false)
    setSkillFilter('')
    setSkillHighlight(0)
  }, [])

  const adjustHeight = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    if (isEmbedded) return
    editor.style.height = 'auto'
    if (
      editor.textContent
      || editor.querySelector('[data-mention-pill]')
      || editor.querySelector('[data-skill-chip]')
    ) {
      const maxHeight = 160
      const newHeight = Math.min(Math.max(editor.scrollHeight, 24), maxHeight)
      editor.style.height = `${newHeight}px`
    }
  }, [isEmbedded])

  const emitContentState = useCallback(() => {
    const editor = editorRef.current
    const plainText = getPlainTextFromEditor(editor)
    // Keep an unchanged restoration across Canvas's initialization remounts,
    // but invalidate on ALL editor mutations, including paste and chip edits
    // that prevent the browser's native input event.
    const restored = deskDraftKey ? deskReturnDrafts.get(deskDraftKey) : undefined
    if (restored && editor && deskDraftKey) {
      const text = serializeNodeText(editor, editor).replace(/\u00A0/g, ' ')
      const chip = editor.querySelector<HTMLElement>('[data-skill-chip="true"]')
      if (text !== restored.text
        || chip?.dataset.skillRef !== restored.skill?.ref
        || chip?.dataset.skillVersionId !== restored.skill?.versionId) {
        deskReturnDrafts.delete(deskDraftKey)
      }
    }
    setHasContent(plainText.length > 0)
    embeddedRef.current?.onTextChange(editor ? serializeNodeText(editor, editor).replace(/\u00A0/g, ' ') : '')
    adjustHeight()
  }, [adjustHeight, deskDraftKey])

  // Only the host's temporary references are authoritative here. Remove their
  // orphan chips in place so text, skill chips, and the caret are preserved.
  useEffect(() => {
    const editor = editorRef.current
    if (!editor || !embeddedCandidates) return
    const activeIds = new Set(embeddedCandidates.map((candidate) => candidate.id))
    const removedIds: string[] = []
    editor.querySelectorAll<HTMLElement>('[data-mention-pill="true"]').forEach((chip) => {
      const id = chip.dataset.nodeId
      if (id && !activeIds.has(id)) {
        removedIds.push(id)
        chip.remove()
      }
    })
    if (removedIds.length > 0) {
      for (const uploadId of removedIds) {
        landingReferenceLog({ event: 'landing-reference-chips:upload-removed', node_id: uploadId, trace_id: `landing-reference:${uploadId}` })
      }
      emitContentState()
      closeDropdown()
    }
  }, [embeddedCandidates, emitContentState, closeDropdown])

  const reconcileSelectedSkillWithEditor = useCallback(() => {
    const chip = editorRef.current?.querySelector<HTMLElement>('[data-skill-chip="true"]')
    // Only called from the editor's input/cut handlers, i.e. a keystroke. A
    // selected skill whose chip is gone was deleted by the user.
    const before = draftStateRef.current.selectedSkill
    if (before && !(chip?.dataset.skillRef === before.ref && chip.dataset.skillVersionId === before.versionId)) {
      trackSkillChipCleared(projectId, before, 'keyboard')
    }
    setSelectedSkill((current) => (
      current
      && chip?.dataset.skillRef === current.ref
      && chip.dataset.skillVersionId === current.versionId
        ? current
        : null
    ))
  }, [projectId])

  const selectSkill = useCallback((skill: DirectorSkillSummary | null, focusEditor = true) => {
    closeSkillDropdown()
    setSelectedSkill(skill)
    const editor = editorRef.current
    if (!editor) return
    replaceDirectorSkillChip(
      editor,
      skill,
    )
    emitContentState()
    if (focusEditor) editor.focus()
  }, [closeSkillDropdown, emitContentState])

  useEffect(() => {
    if (!skillsV2Enabled) {
      setAvailableSkills([])
      // Feature initialization is not a user action. A lazily mounted chat
      // must not steal focus from an editor control (e.g. a timeline curve).
      selectSkill(null, false)
      return
    }

    let cancelled = false
    void listAvailableDirectorSkills()
      .then((skills) => {
        if (!cancelled) setAvailableSkills(skills)
      })
      .catch((reason) => {
        if (cancelled) return
        setAvailableSkills([])
        composerLog({
          event: 'slash-catalog-load-failed',
          trace_id: 'director-skills-slash:catalog',
          outcome: 'error',
          error: reason instanceof Error ? reason.message : String(reason),
        })
      })
    return () => {
      cancelled = true
    }
  }, [selectSkill, skillsV2Enabled])

  const draftStateRef = useRef({ selectedSkill, documents, candidates, documentPending })
  draftStateRef.current = { selectedSkill, documents, candidates, documentPending }
  useLayoutEffect(() => {
    if (!deskDraftKey) return
    const editor = editorRef.current
    if (!editor) return
    const draft = deskReturnDrafts.get(deskDraftKey)
    if (draft) {
      composerLog({ event: 'desk-draft:restored', feature: 'director-desk', scope: deskDraftKey, characters: draft.text.length })
      if (draft.skill) {
        hydrateDirectorSkillDraft(editor, draft.text, draftStateRef.current.candidates, draft.skill, projectId, draft.skillPosition)
      } else {
        hydrateContent(editor, draft.text, draftStateRef.current.candidates, projectId)
      }
      setSelectedSkill(draft.skill)
      setDocuments(draft.documents)
      if (draft.pendingDocumentName) setDocumentError(t('chatInput.documentInterrupted', { filename: draft.pendingDocumentName }))
      emitContentState()
    }
    const capture = () => {
      if (editor.getClientRects().length === 0) return
      const { selectedSkill: skill, documents: attachments } = draftStateRef.current
      const text = serializeNodeText(editor, editor).replace(/\u00A0/g, ' ')
      const chip = editor.querySelector<HTMLElement>('[data-skill-chip="true"]')
      composerLog({ event: 'desk-draft:captured', feature: 'director-desk', scope: deskDraftKey, characters: text.length })
      deskReturnDrafts.set(deskDraftKey, {
        text, skill,
        skillPosition: skill && chip ? getDirectorSkillPosition(editor, chip, text) : undefined,
        documents: attachments,
        pendingDocumentName: draftStateRef.current.documentPending,
      })
    }
    window.addEventListener('dreamwrite:director-desk-opening', capture)
    return () => {
      window.removeEventListener('dreamwrite:director-desk-opening', capture)
    }
  }, [deskDraftKey, projectId, emitContentState, t])

  useImperativeHandle(ref, () => ({
    setText: (text, draft) => {
      const editor = editorRef.current
      if (!editor) return
      const draftSkill = draft === undefined ? selectedSkill : draft.selectedSkill
      if (draftSkill) {
        hydrateDirectorSkillDraft(
          editor,
          text,
          candidates,
          draftSkill,
          projectId,
          draft?.selectedSkillPosition,
        )
      } else {
        hydrateContent(editor, text, candidates, projectId)
      }
      setSelectedSkill(draftSkill)
      emitContentState()
      placeCaretAtEnd(editor)
    },
    focus: () => {
      const editor = editorRef.current
      if (!editor) return
      placeCaretAtEnd(editor)
      editor.focus()
    },
    addMention: (nodeId: string) => {
      const editor = editorRef.current
      if (!editor) return

      // Find the candidate
      const candidate = candidates.find(c => c.id === nodeId)
      if (!candidate) return

      // Skip if already mentioned (dedup)
      const existing = getReferencedNodeIdsFromEditor(editor)
      if (existing.includes(nodeId)) return

      // Insert chip at end
      const selection = window.getSelection()
      if (!selection || !selectionInsideEditor(editor, selection)) {
        placeCaretAtEnd(editor)
      }

      // Insert a space before the chip if needed
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)

        // Check if we need a leading space
        const editorText = editor.textContent ?? ''
        if (editorText.length > 0 && !/\s$/.test(editorText)) {
          const spaceNode = document.createTextNode(' ')
          range.insertNode(spaceNode)
          range.setStartAfter(spaceNode)
          range.collapse(true)
        }

        const chip = createMentionChipElement(candidate, projectId)
        range.insertNode(chip)
        range.setStartAfter(chip)
        range.collapse(true)

        // Add trailing space
        const trailingSpace = document.createTextNode(' ')
        range.insertNode(trailingSpace)
        range.setStartAfter(trailingSpace)
        range.collapse(true)

        sel.removeAllRanges()
        sel.addRange(range)
      }

      emitContentState()
      editor.focus()
    },
  }), [candidates, projectId, emitContentState, selectedSkill])

  // Reset isStopping when status changes from running/polling to something else
  useEffect(() => {
    if (status !== 'running' && status !== 'polling') {
      setIsStopping(false)
    }
  }, [status])

  // Adjust height on mount
  useEffect(() => {
    adjustHeight()
  }, [adjustHeight])

  const emitDirectorMessageSent = useCallback(() => {
    if (!embedded) {
      emitNuxEvent({ type: 'controlActivated', controlId: 'directorMessageSent', anchorId: 'director-input' })
    }
  }, [embedded, emitNuxEvent])

  const handleSend = useCallback((): ChatSendDisposition | false => {
    const editor = editorRef.current
    if (!editor) return false
    const plainText = getPlainTextFromEditor(editor)
    const trimmed = plainText.trim()
    // A document alone is a legitimate turn ("here's my script") — requiring
    // typed text would force busywork after attaching.
    if ((!trimmed && documents.length === 0 && !embedded?.allowEmptySend) || disabled) return false

    const skillChip = editor.querySelector<HTMLElement>('[data-skill-chip="true"]')
    // Serialize node references as @[[nodeId]] markers and the selected skill
    // as [[/public-slug]]. The immutable skill ref/version still travel as
    // structured metadata; the marker preserves the sentence's inline meaning
    // for both persisted history and the Director model.
    const serializedRaw = serializeNodeText(editor, editor).replace(/\u00A0/g, ' ')
    const serialized = serializedRaw.trim()
    const referencedNodeIds = getReferencedNodeIdsFromEditor(editor)

    const composedMessage = composeWithDocs(serialized, documents)
    const attachments = documents.length > 0 ? [...documents] : undefined
    const references = referencedNodeIds.length > 0 ? referencedNodeIds : undefined
    const visibleSelectedSkill = selectedSkill
      && skillChip?.dataset.skillRef === selectedSkill.ref
      && skillChip.dataset.skillVersionId === selectedSkill.versionId
      ? selectedSkill
      : undefined
    const selectedSkillPosition = visibleSelectedSkill && skillChip
      ? getDirectorSkillPosition(editor, skillChip, serializedRaw)
      : undefined
    if (selectedSkill && !visibleSelectedSkill) setSelectedSkill(null)
    if (visibleSelectedSkill && selectedSkillPosition !== undefined) {
      const marker = directorSkillSerializedMarker(visibleSelectedSkill)
      const markerAtPosition = directorSkillSerializedMarkers(visibleSelectedSkill).some(
        (candidate) =>
          serialized.slice(selectedSkillPosition, selectedSkillPosition + candidate.length) === candidate,
      )
      directorSkillLog({
        event: 'inline-marker-serialized',
        trace_id: `director-skills-send:${visibleSelectedSkill.skillId}`,
        skill_ref: visibleSelectedSkill.ref,
        public_slug: directorSkillPublicSlug(visibleSelectedSkill),
        technical_name: visibleSelectedSkill.name,
        marker,
        inline_position: selectedSkillPosition,
        marker_at_position: markerAtPosition,
      })
      if (!markerAtPosition) {
        throw new Error('Serialized Director skill marker does not match its inline position')
      }
    }
    const disposition = visibleSelectedSkill
      ? onSend(
          composedMessage,
          attachments,
          references,
          visibleSelectedSkill,
          selectedSkillPosition,
        )
      : onSend(composedMessage, attachments, references)
    if (disposition === false) return false
    if (disposition !== 'sent' && disposition !== 'queued') {
      throw new Error(
        `ChatInput onSend returned invalid disposition: expected "sent", "queued", or false; received ${String(disposition)}`,
      )
    }
    emitDirectorMessageSent()

    // Landing owns asynchronous creation; keep its draft available on failure.
    if (embedded) return disposition

    // Clear only after the owner accepted the send intent. During Director
    // activity AgentChat first commits the item to its visible scoped queue.
    editor.innerHTML = ''
    if (deskDraftKey) deskReturnDrafts.delete(deskDraftKey)
    setSelectedSkill(null)
    setHasContent(false)
    // New turn: any extraction still in flight belongs to the message just
    // sent and must not attach itself to the next one.
    documentTurnRef.current += 1
    setDocuments([])
    setDocumentPending(null)
    setDocumentError(null)
    closeDropdown()
    closeSkillDropdown()
    adjustHeight()
    return disposition
  }, [onSend, disabled, embedded, emitDirectorMessageSent, deskDraftKey, adjustHeight, closeDropdown, closeSkillDropdown, documents, selectedSkill])

  // Mention trigger detection
  const syncMentionTrigger = useCallback(() => {
    const editor = editorRef.current
    if (!editor || candidates.length === 0 || composingRef.current) {
      closeDropdown()
      return
    }

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
      closeDropdown()
      return
    }

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    if (textNode.nodeType !== Node.TEXT_NODE) {
      closeDropdown()
      return
    }

    const text = textNode.textContent ?? ''
    const cursorPos = range.startOffset

    let atPos = -1
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = text[i]
      if (char === '@') {
        atPos = i
        break
      }
      if (char === '\n' || char === '\r') break
    }

    mentionLog('syncMentionTrigger text=%s cursorPos=%d atPos=%d charBefore=%s', text, cursorPos, atPos, atPos > 0 ? text[atPos - 1] : '(none)')

    if (atPos >= 0) {
      closeSkillDropdown()
      anchorRef.current = { node: textNode, offset: atPos }
      const query = text.substring(atPos + 1, cursorPos)
      setMentionFilter(query)
      setShowMentionDropdown(true)
      setMentionHighlight(0)

      // Close if no candidates match
      const queryLower = query.toLowerCase()
      const hasMatch = queryLower === '' || candidates.some(
        (c) => c.label.toLowerCase().includes(queryLower) || c.type.toLowerCase().includes(queryLower),
      )
      if (!hasMatch) {
        closeDropdown()
      }
      return
    }

    closeDropdown()
  }, [candidates, closeDropdown, closeSkillDropdown])

  // Insert a mention chip
  const insertMention = useCallback((candidate: MentionCandidate) => {
    const editor = editorRef.current
    const anchor = anchorRef.current
    if (!editor || !anchor) {
      closeDropdown()
      return
    }
    noteChatReference(candidate.id, 'typed_mention')

    const anchorNode = anchor.node
    if (anchorNode.nodeType !== Node.TEXT_NODE || !anchorNode.parentNode) {
      closeDropdown()
      return
    }

    const fullText = anchorNode.textContent ?? ''
    const mentionLength = 1 + mentionFilter.length
    const before = fullText.substring(0, anchor.offset)
    const after = fullText.substring(anchor.offset + mentionLength)
    const parent = anchorNode.parentNode

    const beforeNode = document.createTextNode(before)
    const chip = createMentionChipElement(candidate, projectId)
    const trailingSpacer = after.length === 0 || /^\s/.test(after) ? '' : ' '
    const afterNode = document.createTextNode(`${trailingSpacer}${after}`)

    parent.insertBefore(beforeNode, anchorNode)
    parent.insertBefore(chip, anchorNode)
    parent.insertBefore(afterNode, anchorNode)
    parent.removeChild(anchorNode)

    // Place cursor after chip
    const selection = window.getSelection()
    if (selection) {
      const range = document.createRange()
      range.setStart(afterNode, trailingSpacer.length)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    emitContentState()
    closeDropdown()
    if (isEmbedded) {
      landingReferenceLog({ event: 'landing-reference-chips:inserted', node_id: candidate.id, trace_id: `landing-reference:${candidate.id}` })
    }
    editor.focus()
  }, [mentionFilter, closeDropdown, emitContentState, projectId, isEmbedded])

  // Chip removal
  const removeChip = useCallback((chip: HTMLElement) => {
    if (isEmbedded) {
      landingReferenceLog({ event: 'landing-reference-chips:removed', node_id: chip.dataset.nodeId, trace_id: `landing-reference:${chip.dataset.nodeId}` })
    }
    chip.remove()
    const editor = editorRef.current
    if (editor && editor.textContent === '') {
      editor.innerHTML = ''
    }
    emitContentState()
    if (editor) placeCaretAtEnd(editor)
  }, [emitContentState, isEmbedded])

  const syncSkillSlashTrigger = useCallback(() => {
    const editor = editorRef.current
    const selection = window.getSelection()
    if (
      !editor
      || availableSkills.length === 0
      || composingRef.current
      || !selectionInsideEditor(editor, selection)
      || !selection
      || selection.rangeCount === 0
    ) {
      closeSkillDropdown()
      return false
    }

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    if (textNode.nodeType !== Node.TEXT_NODE || !textNode.parentNode) {
      closeSkillDropdown()
      return false
    }

    const beforeCaret = (textNode.textContent ?? '').slice(0, range.startOffset)
    const match = beforeCaret.match(/\/([a-z0-9_-]{0,64})$/i)
    if (!match) {
      closeSkillDropdown()
      return false
    }

    const slashStart = beforeCaret.length - match[1].length - 1
    if (slashStart > 0 && !/\s/.test(beforeCaret[slashStart - 1])) {
      closeSkillDropdown()
      return false
    }

    const query = match[1]
    const normalizedQuery = query.toLocaleLowerCase()
    const matches = availableSkills.filter((skill) =>
      `${skill.displayName} ${directorSkillPublicSlug(skill)} ${skill.description}`.toLocaleLowerCase().includes(normalizedQuery),
    )
    if (matches.length === 0) {
      closeSkillDropdown()
      return false
    }

    closeDropdown()
    skillAnchorRef.current = { node: textNode, offset: slashStart }
    setSkillFilter(query)
    setShowSkillDropdown(true)
    setSkillHighlight(0)
    return true
  }, [availableSkills, closeDropdown, closeSkillDropdown])

  const insertSkillFromTypeahead = useCallback((skill: DirectorSkillSummary) => {
    const editor = editorRef.current
    const anchor = skillAnchorRef.current
    if (!editor || !anchor || anchor.node.nodeType !== Node.TEXT_NODE) {
      closeSkillDropdown()
      return
    }

    const anchorNode = anchor.node
    const commandEnd = anchor.offset + 1 + skillFilter.length
    if (!anchorNode.parentNode || commandEnd > (anchorNode.textContent ?? '').length) {
      closeSkillDropdown()
      return
    }

    const commandRange = document.createRange()
    commandRange.setStart(anchorNode, anchor.offset)
    commandRange.setEnd(anchorNode, commandEnd)
    commandRange.deleteContents()

    removeDirectorSkillChips(editor)
    const chip = createDirectorSkillChipElement(skill)
    const spacer = document.createTextNode(' ')
    const fragment = document.createDocumentFragment()
    fragment.append(chip, spacer)
    commandRange.insertNode(fragment)

    const selection = window.getSelection()
    if (selection) {
      const nextRange = document.createRange()
      nextRange.setStartAfter(spacer)
      nextRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(nextRange)
    }

    setSelectedSkill(skill)
    const menuIndex = filteredSkills.findIndex((item) => item.ref === skill.ref && item.versionId === skill.versionId)
    trackSlashSkillSelected(projectId, skill, menuIndex >= 0 ? menuIndex + 1 : undefined)
    emitContentState()
    closeSkillDropdown()
    editor.focus()
    composerLog({
      event: 'slash-command-resolved',
      trace_id: `director-skills-slash:${skill.skillId}`,
      requested: skillFilter,
      resolved: skill.ref,
      inline_position: getDirectorSkillPosition(editor, chip),
      outcome: 'selected',
    })
  }, [closeSkillDropdown, emitContentState, filteredSkills, projectId, skillFilter])

  const removeAdjacentChip = useCallback((direction: 'backward' | 'forward') => {
    const editor = editorRef.current
    if (!editor) return false

    const selection = window.getSelection()
    if (!selectionInsideEditor(editor, selection) || !selection || selection.rangeCount === 0) {
      return false
    }

    const range = selection.getRangeAt(0)
    if (!range.collapsed) return false

    let adjacentNode: globalThis.Node | null = null
    if (range.startContainer instanceof Text) {
      const textNode = range.startContainer
      if (direction === 'backward' && range.startOffset === 0) {
        adjacentNode = textNode.previousSibling
      } else if (
        direction === 'forward' &&
        range.startOffset === (textNode.textContent ?? '').length
      ) {
        adjacentNode = textNode.nextSibling
      }
    } else if (range.startContainer instanceof HTMLElement) {
      const container = range.startContainer
      const childIndex =
        direction === 'backward' ? range.startOffset - 1 : range.startOffset
      adjacentNode = container.childNodes[childIndex] ?? null
    }

    if (
      adjacentNode instanceof HTMLElement &&
      adjacentNode.dataset.mentionPill === 'true'
    ) {
      removeChip(adjacentNode)
      return true
    }
    if (
      adjacentNode instanceof HTMLElement &&
      adjacentNode.dataset.skillChip === 'true'
    ) {
      selectSkill(null)
      return true
    }

    return false
  }, [removeChip, selectSkill])

  // Input handler
  const handleInput = useCallback(() => {
    if (composingRef.current) return
    reconcileSelectedSkillWithEditor()
    emitContentState()
    if (syncSkillSlashTrigger()) return
    syncMentionTrigger()
  }, [emitContentState, reconcileSelectedSkillWithEditor, syncMentionTrigger, syncSkillSlashTrigger])

  const handleCompositionStart = useCallback(() => {
    composingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false
    handleInput()
  }, [handleInput])

  // Serialize selection for copy/cut
  const serializeSelection = useCallback((): string | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null

    const range = selection.getRangeAt(0)
    const fragment = range.cloneContents()
    const container = document.createElement('div')
    container.appendChild(fragment)

    const serialized = serializeNodeText(container, container, {
      selectionFragment: true,
    })
    return serialized || null
  }, [])

  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)
  }, [serializeSelection])

  const handleCut = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const serialized = serializeSelection()
    if (!serialized) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', serialized)

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      selection.getRangeAt(0).deleteContents()
    }

    const editor = editorRef.current
    if (editor && editor.textContent === '') {
      editor.innerHTML = ''
    }
    reconcileSelectedSkillWithEditor()
    emitContentState()
  }, [emitContentState, reconcileSelectedSkillWithEditor, serializeSelection])

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const editor = editorRef.current
    if (!editor) return

    const text = e.clipboardData.getData('text/plain')
    if (!text) return

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!range.collapsed) {
      range.deleteContents()
    }

    const MARKER_RE = /@\[\[([^\]]+)\]\]/g
    const hasMarkers = MARKER_RE.test(text)

    if (hasMarkers) {
      MARKER_RE.lastIndex = 0
      const frag = document.createDocumentFragment()
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = MARKER_RE.exec(text)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
        }
        const nodeId = match[1]
        const candidate = candidates.find((c) => c.id === nodeId)
        if (candidate) {
          frag.appendChild(createMentionChipElement(candidate, projectId))
        } else {
          frag.appendChild(document.createTextNode(match[0]))
        }
        lastIndex = match.index + match[0].length
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)))
      }

      const lastChild = frag.lastChild
      range.insertNode(frag)

      if (lastChild) {
        range.setStartAfter(lastChild)
        range.collapse(true)
      } else {
        range.collapse(false)
      }
      selection.removeAllRanges()
      selection.addRange(range)
    } else {
      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    emitContentState()
  }, [candidates, emitContentState, projectId])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Ignore keystrokes during IME composition
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return

    if (showSkillDropdown && filteredSkills.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSkillHighlight((previous) => (previous + 1) % filteredSkills.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSkillHighlight(
          (previous) => (previous - 1 + filteredSkills.length) % filteredSkills.length,
        )
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertSkillFromTypeahead(filteredSkills[skillHighlight])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        closeSkillDropdown()
        return
      }
    }

    // Mention dropdown navigation
    if (showMentionDropdown && filteredCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionHighlight(prev => {
          const next = (prev + 1) % filteredCandidates.length
          onMentionClick?.(filteredCandidates[next].id)
          return next
        })
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionHighlight(prev => {
          const next = (prev - 1 + filteredCandidates.length) % filteredCandidates.length
          onMentionClick?.(filteredCandidates[next].id)
          return next
        })
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(filteredCandidates[mentionHighlight])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        closeDropdown()
        return
      }
    }

    if (
      e.key === 'ArrowUp' &&
      onRetrieveQueuedPrompt &&
      !getPlainTextFromEditor(editorRef.current).trim()
    ) {
      const retrieved = onRetrieveQueuedPrompt()
      if (retrieved) {
        e.preventDefault()
        return
      }
    }

    // Chip removal via Backspace/Delete
    if (e.key === 'Backspace' && removeAdjacentChip('backward')) {
      e.preventDefault()
      return
    }
    if (e.key === 'Delete' && removeAdjacentChip('forward')) {
      e.preventDefault()
      return
    }

    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.altKey &&
      (embedded ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey)
    ) {
      e.preventDefault()
      const disposition = handleSend()
      const accepted = disposition !== false
      composerLog({
        event: 'composer:enter-key-resolved',
        trace_id: `chat-composer-enter:${status}`,
        status,
        current_status: status,
        requested: 'send-message',
        resolved: disposition === 'queued'
          ? 'send-enqueued'
          : disposition === 'sent'
            ? 'send-dispatched'
            : 'send-rejected',
        intent: 'send-message',
        outcome: disposition === 'queued'
          ? 'queued'
          : disposition === 'sent'
            ? 'dispatched'
            : 'rejected',
        block_reason: disabled ? 'composer-disabled' : accepted ? null : 'empty-draft',
        execution_active: isExecutionActive,
        send_intent_accepted: accepted,
        send_dispatched: disposition === 'sent',
        enqueued: disposition === 'queued',
        draft_preserved: !accepted,
        draft_cleared: accepted,
        stop_requested: false,
      })
    }
  }, [closeDropdown, closeSkillDropdown, disabled, embedded, filteredCandidates, filteredSkills, handleSend, insertMention, insertSkillFromTypeahead, isExecutionActive, mentionHighlight, onMentionClick, onRetrieveQueuedPrompt, removeAdjacentChip, showMentionDropdown, showSkillDropdown, skillHighlight, status])

  // Click handling for chips
  const handleEditorClick = useCallback((event: MouseEvent) => {
    if (!(event.target instanceof HTMLElement)) return
    const target = event.target
    if (disabled && target.closest('[data-skill-chip-remove], [data-mention-chip-remove]')) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    const skillRemoveButton = target.closest('[data-skill-chip-remove]')
    if (skillRemoveButton) {
      event.preventDefault()
      event.stopPropagation()
      const cleared = draftStateRef.current.selectedSkill
      if (cleared) trackSkillChipCleared(projectId, cleared, 'selected_chip')
      selectSkill(null)
      return
    }

    const removeButton = target.closest('[data-mention-chip-remove]')
    if (removeButton) {
      event.preventDefault()
      event.stopPropagation()
      const chip = removeButton.closest('[data-mention-pill]')
      if (chip instanceof HTMLElement) removeChip(chip)
      return
    }

    const chipLabel = target.closest('[data-mention-chip-label]')
    if (chipLabel) {
      const chip = chipLabel.closest('[data-mention-pill]')
      if (chip instanceof HTMLElement && chip.dataset.nodeId) {
        event.preventDefault()
        event.stopPropagation()
        onMentionClick?.(chip.dataset.nodeId)
      }
    }
  }, [onMentionClick, removeChip, selectSkill, projectId, disabled])

  // Close composer typeaheads on outside click
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const editor = editorRef.current
      const dropdown = dropdownRef.current
      const skillDropdown = skillDropdownRef.current
      if (!(event.target instanceof globalThis.Node)) return
      const target = event.target
      if (!editor) return
      if (
        !editor.contains(target)
        && !(dropdown && dropdown.contains(target))
        && !(skillDropdown && skillDropdown.contains(target))
      ) {
        closeDropdown()
        closeSkillDropdown()
      }
    }

    const editor = editorRef.current
    if (!editor) return

    editor.addEventListener('click', handleEditorClick)
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      editor.removeEventListener('click', handleEditorClick)
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [closeDropdown, closeSkillDropdown, handleEditorClick])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!showMentionDropdown || !dropdownRef.current) return
    const items = dropdownRef.current.querySelectorAll('[data-mention-item]')
    items[mentionHighlight]?.scrollIntoView({ block: 'nearest' })
  }, [mentionHighlight, showMentionDropdown])

  useEffect(() => {
    if (!showSkillDropdown || !skillDropdownRef.current) return
    const items = skillDropdownRef.current.querySelectorAll('[data-skill-typeahead-item]')
    items[skillHighlight]?.scrollIntoView({ block: 'nearest' })
  }, [showSkillDropdown, skillHighlight])


  // A document alone is a valid turn, so the button must follow the same rule
  // handleSend does — otherwise Enter works and clicking does not. Extraction
  // in flight keeps it disabled: sending then would race the pending result.
  const canSend = (hasContent || documents.length > 0) && !documentPending && !disabled

  const skillPicker = skillsV2Enabled && (
    <SkillPicker
      selectedSkill={selectedSkill}
      onSelect={selectSkill}
      onCreateWithDirector={() => {
        const disposition = onSend(t('skillPicker.authoringPrompt'))
        if (disposition === 'sent' || disposition === 'queued') emitDirectorMessageSent()
      }}
      onEditWithDirector={(skill) => {
        const disposition = onSend(
          t('skillPicker.editWithDirectorPrompt', {
            name: skill.displayName,
            ref: skill.ref,
            version: skill.version,
          }),
          undefined,
          undefined,
          embedded ? undefined : skill,
        )
        if (disposition === 'sent' || disposition === 'queued') emitDirectorMessageSent()
      }}
      disabled={disabled}
      triggerClassName={embedded?.skillTriggerClassName ?? COMPOSER_TRIGGER_CLASS}
      triggerIconClassName={embedded?.skillTriggerIconClassName}
      showTriggerLabel={Boolean(embedded)}
      onTriggerMouseEnter={() => logComposerTriggerHover('skill')}
    />
  )

  return (
    <div className={embedded ? "contents" : "flex-shrink-0 p-3 border-t border-border-subtle"}>
      {/* Main input container */}
      <div
        data-nux-anchor={embedded ? undefined : 'director-input'}
        className={embedded ? "contents" : `
          relative rounded-lg border transition-all duration-150
          ${isFocused
            ? 'border-chrome/60 ring-2 ring-chrome/20 bg-chrome/10'
            : 'border-chrome/30 bg-chrome/5 hover:border-chrome/50 hover:bg-chrome/[0.08]'
          }
          ${disabled ? 'opacity-50' : ''}
        `}
      >
        {/* Portal-based mention dropdown */}
        {showMentionDropdown && (
          <MentionDropdownPortal
            candidates={filteredCandidates}
            highlightIndex={mentionHighlight}
            projectId={projectId}
            editorRef={editorRef}
            dropdownRef={dropdownRef}
            onSelect={insertMention}
            onHighlight={setMentionHighlight}
          />
        )}
        {showSkillDropdown && (
          <SkillTypeaheadPortal
            skills={filteredSkills}
            highlightIndex={skillHighlight}
            editorRef={editorRef}
            dropdownRef={skillDropdownRef}
            label={t('skillPicker.choose')}
            onSelect={insertSkillFromTypeahead}
            onHighlight={setSkillHighlight}
          />
        )}

        {/* Attached documents (RFC-1218 PR1) — text already extracted. */}
        {(documents.length > 0 || documentPending || documentError) && (
          <div className="flex flex-col gap-1 px-3 pt-2" data-testid="chat-document-attachments">
            <div className="flex flex-wrap gap-1.5">
              {documents.map((doc, index) => (
                <span
                  key={`${doc.filename}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-2 px-2 py-1 text-xs font-medium text-text-secondary"
                  data-testid="chat-document-chip"
                >
                  <FileText className="h-3.5 w-3.5 text-text-muted" strokeWidth={1.5} />
                  <span className="max-w-[180px] truncate" title={doc.filename}>
                    {doc.filename}
                  </span>
                  <span className="text-text-muted">
                    {t('chatInput.documentChars', { count: doc.charCount })}
                  </span>
                  {/* Screening is invisible when it passes, which makes the
                      feature feel unguarded. Showing the cleared count is the
                      only signal a user gets that their images were checked. */}
                  {doc.clearedImageCount > 0 && (
                    <span className="text-text-muted" data-testid="chat-document-images">
                      {t('chatInput.documentImagesChecked', { count: doc.clearedImageCount })}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (deskDraftKey) deskReturnDrafts.delete(deskDraftKey)
                      setDocuments((previous) => previous.filter((_, i) => i !== index))
                    }}
                    className="rounded text-text-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome"
                    aria-label={t('chatInput.removeDocument', { filename: doc.filename })}
                    data-testid="chat-document-remove"
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
              {documentPending && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-2 px-2 py-1 text-xs font-medium text-text-muted"
                  data-testid="chat-document-pending"
                >
                  {t('chatInput.documentExtracting', { filename: documentPending })}
                </span>
              )}
            </div>
            {documentError && (
              <p className="text-xs font-medium text-destructive" data-testid="chat-document-error">
                {documentError}
              </p>
            )}
          </div>
        )}

        {/* contentEditable editor */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-label={embedded ? t('chatInput.defaultPlaceholder') : effectivePlaceholder}
          aria-multiline="true"
          aria-autocomplete="list"
          aria-expanded={showMentionDropdown || showSkillDropdown}
          aria-controls={
            showSkillDropdown
              ? 'chat-skill-dropdown'
              : showMentionDropdown
                ? 'chat-mention-dropdown'
                : undefined
          }
          data-testid={embedded ? "hero-prompt" : "chat-input-editor"}
          data-placeholder={effectivePlaceholder}
          className={embedded ? `chat-input__editor ${embedded.editorClassName}` : `chat-input__editor w-full bg-transparent px-4 pt-3 pb-2 text-base text-foreground focus:outline-none overflow-y-auto whitespace-pre-wrap break-words ${
            disabled ? 'cursor-not-allowed' : ''
          }`}
          style={embedded ? undefined : { minHeight: '24px', maxHeight: '160px' }}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onCopy={handleCopy}
          onCut={handleCut}
          onPaste={handlePaste}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />

        <style>{`
          .chat-input__editor:empty::before {
            content: attr(data-placeholder);
            /* Bright text contrasts against the amber-tinted composer bg;
               opacity keeps it clearly placeholder-not-input. */
            color: var(--foreground, #ffffff);
            opacity: 0.72;
            pointer-events: none;
          }
        `}</style>

        {/* Action bar */}
        {embedded ? embedded.renderToolbar(skillPicker, () => { handleSend() }) : <div className="flex items-center justify-between px-2 pb-2">
          {/* Left side - Upload + @ mention + Skill */}
          <div className="flex items-center gap-1 relative">
            {/* Upload button with flyover popover */}
            {onUploadMedia && (
              <UploadFlyout
                onUploadMedia={(accepted, rejected) => {
                  for (const kind of UPLOAD_MEDIA_KINDS) {
                    const n = accepted.filter((item) => item.type === kind).length
                    if (n > 0) trackInteraction('chat_attachment_added', { project_id: projectId ?? undefined, attachment_type: kind, file_count_bucket: countBucket(n) })
                  }
                  onUploadMedia(accepted, rejected)
                }}
                onUploadDocument={(file) => {
                  trackInteraction('chat_attachment_added', { project_id: projectId ?? undefined, attachment_type: 'document', file_count_bucket: countBucket(1) })
                  void handleDocumentSelected(file)
                }}
                disabled={disabled}
                testIdPrefix="chat-upload"
                buttonClassName={COMPOSER_TRIGGER_CLASS}
                onButtonMouseEnter={() => logComposerTriggerHover('upload')}
                onMenuOpen={(optionCount) => trackChatUploadMenuViewed(projectId, optionCount)}
              />
            )}

            {/* @ mention button */}
            {canvasNodes && canvasNodes.length > 0 && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  const editor = editorRef.current
                  if (!editor) return

                  // Place cursor inside editor if not already
                  const selection = window.getSelection()
                  if (!selection || !selectionInsideEditor(editor, selection)) {
                    placeCaretAtEnd(editor)
                  }

                  // Check if we need a space before @
                  const sel = window.getSelection()
                  if (!sel || sel.rangeCount === 0) return
                  const range = sel.getRangeAt(0)

                  const editorText = editor.textContent ?? ''
                  const needsSpace = editorText.length > 0 && !/\s$/.test(editorText)

                  const textToInsert = needsSpace ? ' @' : '@'
                  const textNode = document.createTextNode(textToInsert)
                  range.insertNode(textNode)
                  // Keep cursor inside the text node so syncMentionTrigger()
                  // finds a TEXT_NODE (not the parent element).
                  range.setStart(textNode, textToInsert.length)
                  range.setEnd(textNode, textToInsert.length)
                  sel.removeAllRanges()
                  sel.addRange(range)

                  editor.focus()
                  // Trigger mention detection after the browser
                  // has committed the selection update.
                  emitContentState()
                  requestAnimationFrame(() => {
                    syncMentionTrigger()
                  })
                }}
                onMouseEnter={() => logComposerTriggerHover('mention')}
                className={COMPOSER_TRIGGER_CLASS}
                title={t('chatInput.mentionTooltip')}
              >
                <AtSign className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}

            {skillPicker}

          </div>

          {/* Right side - send button with status indicator */}
          {/* RFC 0061: Stop button when running */}
          {isExecutionActive ? (
            <button
              onClick={async () => {
                const stopHandlerRegistered = onStop !== undefined
                composerLog({
                  event: 'composer:stop-requested',
                  trace_id: `chat-composer-stop:${status}`,
                  status,
                  current_status: status,
                  requested: 'stop-execution',
                  resolved: stopHandlerRegistered
                    ? 'stop-request-dispatched'
                    : 'stop-handler-missing',
                  outcome: stopHandlerRegistered ? 'requested' : 'rejected',
                  trigger: 'stop-button',
                  execution_active: isExecutionActive,
                  stop_requested: stopHandlerRegistered,
                  cancel_requested: stopHandlerRegistered,
                })
                setIsStopping(true)
                try {
                  await onStop?.()
                } finally {
                  setIsStopping(false)
                }
              }}
              disabled={isStopping}
              className={`p-2 rounded-md transition-colors flex items-center gap-2 ${
                isStopping
                  ? 'bg-destructive/10 text-destructive/50 cursor-not-allowed'
                  : 'bg-destructive/20 text-destructive hover:bg-destructive/30'
              }`}
              title={isStopping ? t('chatInput.stoppingTitle') : t('chatInput.stopTitle')}
            >
              {/* Stop icon (square) */}
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
              {isStopping ? (
                <span className="text-xs font-medium">{t('chatInput.stoppingLabel')}</span>
              ) : status === 'polling' ? (
                <span className="text-xs font-medium opacity-70">{t('chatInput.pollingLabel')}</span>
              ) : null}
            </button>
          ) : status === 'pending_questions' ? (
            <div
              className="p-2 rounded-md bg-warning/10 text-warning"
              title={t('chatInput.answerQuestions')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`
                p-2 rounded-md transition-colors duration-150
                ${canSend
                  ? 'bg-chrome text-background hover:bg-chrome/90'
                  : 'bg-chrome/30 text-background/60 cursor-not-allowed'
                }
              `}
              title={t('chatInput.sendMessage')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          )}
        </div>}
      </div>

      {/* Helper text */}
      {!embedded && <div className="mt-2 text-xs font-medium text-text-muted text-center">
        {t('chatInput.helperText')}
        {canvasNodes && canvasNodes.length > 0 && (
          <> {t('chatInput.helperMention')}</>
        )}
      </div>}
    </div>
  )
})
